import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import data from '../../../../data/mcsa/games/notification-rush.json';

interface NotificationRushProps {
    onComplete: () => void;
    muted?: boolean;
}

type TMeaning = 'urgent' | 'routine' | 'spam';
type TColor = 'blue' | 'vermillion' | 'grey';
type TAction = 'respond_now' | 'wait' | null;
type TScreen = 'intro' | 'game' | 'complete';

interface IItem { sender: string; text: string; }
interface ITrial {
    meaning: TMeaning;
    colorShown: TColor;
    correct: 'respond_now' | 'wait' | 'ignore';
    sender: string;
    text: string;
    deadlineMs: number;
    blockName: string;
    explanation?: string;
    congruency?: string;
    blockHalf?: string;
}

interface ILog {
    meaning: TMeaning;
    congruency?: string;
    computedOutcome: string;
    responseTimeMs: number | null;
}

interface IModal {
    title: string;
    body: string;
    actionLabel: string;
    badgeText?: string | null;
    badgeCorrect?: boolean | null;
    onConfirm: () => void;
}

const BLOCKS = data.blocks as { name: string; deadline: number }[];
const CONTENT = data.content as { urgent: IItem[]; routine: IItem[]; spam: IItem[] };
const PRACTICE = data.practice as ITrial[];

const COLOR_HEX: Record<TColor, string> = {
    blue: '#1467A8',
    vermillion: '#B8500A',
    grey: '#64748B',
};

const rand = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];
const shuffle = <T,>(arr: T[]) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const makeGo = (meaning: 'urgent' | 'routine', colorShown: TColor, block: { name: string; deadline: number }): ITrial => {
    const item = rand(meaning === 'urgent' ? CONTENT.urgent : CONTENT.routine);
    const congruent = (meaning === 'urgent' && colorShown === 'blue') || (meaning === 'routine' && colorShown === 'vermillion');
    return {
        meaning,
        colorShown,
        correct: meaning === 'urgent' ? 'respond_now' : 'wait',
        congruency: congruent ? 'congruent' : 'incongruent',
        sender: item.sender,
        text: item.text,
        deadlineMs: block.deadline,
        blockName: block.name,
    };
};

const makeSpam = (colorShown: TColor, block: { name: string; deadline: number }): ITrial => {
    const item = rand(CONTENT.spam);
    return { meaning: 'spam', colorShown, correct: 'ignore', congruency: 'n/a', sender: item.sender, text: item.text, deadlineMs: block.deadline, blockName: block.name };
};

const generateBlock = (block: { name: string; deadline: number }) => {
    const first: ITrial[] = [];
    for (let i = 0; i < 4; i++) first.push(makeGo('urgent', 'blue', block));
    for (let i = 0; i < 4; i++) first.push(makeGo('routine', 'vermillion', block));
    for (let i = 0; i < 2; i++) first.push(makeSpam(Math.random() < 0.5 ? 'blue' : 'vermillion', block));
    const second: ITrial[] = [];
    for (let i = 0; i < 4; i++) {
        const inc = Math.random() < 0.3;
        second.push(makeGo('urgent', inc ? 'vermillion' : 'blue', block));
    }
    for (let i = 0; i < 4; i++) {
        const inc = Math.random() < 0.3;
        second.push(makeGo('routine', inc ? 'blue' : 'vermillion', block));
    }
    for (let i = 0; i < 2; i++) second.push(makeSpam(Math.random() < 0.5 ? 'blue' : 'vermillion', block));
    return [
        ...shuffle(first).map((t) => ({ ...t, blockHalf: 'first' })),
        ...shuffle(second).map((t) => ({ ...t, blockHalf: 'second' })),
    ];
};

export const NotificationRush = ({ onComplete, muted = false }: NotificationRushProps) => {
    const [screen, setScreen] = useState<TScreen>('intro');
    const [clock, setClock] = useState('09:41');
    const [date, setDate] = useState('');
    const [soundOn, setSoundOn] = useState(true);
    const [trial, setTrial] = useState<ITrial | null>(null);
    const [badge, setBadge] = useState('Warm-up');
    const [modal, setModal] = useState<IModal | null>(null);
    const [stats, setStats] = useState<{ accuracy: string; spam: string; affirmationTitle: string; insightText: string } | null>(null);
    const [ringOffset, setRingOffset] = useState(0);

    const acceptingRef = useRef(false);
    const trialStartRef = useRef(0);
    const animRef = useRef<number | null>(null);
    const isPracticeRef = useRef(false);
    const practiceStepRef = useRef(0);
    const blockIdxRef = useRef(0);
    const trialInBlockRef = useRef(0);
    const blockTrialsRef = useRef<ITrial[]>([]);
    const logRef = useRef<ILog[]>([]);
    const trialRef = useRef<ITrial | null>(null);
    const audioRef = useRef<AudioContext | null>(null);
    const strokeLen = 1080;

    trialRef.current = trial;

    useEffect(() => {
        const tick = () => {
            const n = new Date();
            setClock(`${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}`);
            setDate(n.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }));
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, []);

    const playSound = (freq: number, dur: number, type: OscillatorType = 'sine') => {
        if (muted || !soundOn) return;
        try {
            if (!audioRef.current) audioRef.current = new AudioContext();
            const ctx = audioRef.current;
            if (ctx.state === 'suspended') ctx.resume();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = type;
            osc.frequency.setValueAtTime(freq, ctx.currentTime);
            gain.gain.setValueAtTime(0.08, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + dur);
        } catch {
            return;
        }
    };

    const startTimer = (deadline: number) => {
        if (animRef.current) cancelAnimationFrame(animRef.current);
        trialStartRef.current = performance.now();
        acceptingRef.current = true;
        setRingOffset(0);
        const loop = (now: number) => {
            const elapsed = now - trialStartRef.current;
            const remaining = Math.max(0, 1 - elapsed / deadline);
            setRingOffset((1 - remaining) * strokeLen);
            if (elapsed >= deadline) respond(null);
            else animRef.current = requestAnimationFrame(loop);
        };
        animRef.current = requestAnimationFrame(loop);
    };

    const renderTrial = (t: ITrial, nextBadge: string) => {
        setTrial(t);
        setBadge(nextBadge);
        requestAnimationFrame(() => startTimer(t.deadlineMs));
    };

    const showCompletion = () => {
        const log = logRef.current;
        const total = log.length;
        const correct = log.filter((t) => t.computedOutcome === 'hit' || t.computedOutcome === 'correct-inhibition').length;
        const accuracy = total ? Math.round((correct / total) * 100) : 0;
        const spam = log.filter((t) => t.meaning === 'spam');
        const spamOk = spam.filter((t) => t.computedOutcome === 'correct-inhibition').length;
        const spamRate = spam.length ? Math.round((spamOk / spam.length) * 100) : 0;
        const affirmationTitle = accuracy >= 90 ? 'Sharp instincts!' : accuracy >= 75 ? 'Nicely handled!' : 'Solid effort and you kept going!';
        let insightText: string;
        if (spamRate >= 75) insightText = "You read the message, not the label — that's exactly the reflex that trips up scammers. Junk got the silent treatment while the real stuff got through.";
        else if (spamRate >= 40) insightText = "You caught most of the traps, but a few bits of spam still slipped past. Under pressure, an urgent-looking tone can pull you in — pausing half a beat is your edge.";
        else insightText = "Spam leaned on urgency and color to rush you, and a few got a tap. Noticing that pull is the whole skill and you'll spot it faster next time.";
        setStats({ accuracy: `${accuracy}%`, spam: `${spamRate}%`, affirmationTitle, insightText });
        setScreen('complete');
    };

    const launchTrial = useCallback(() => {
        if (trialInBlockRef.current >= blockTrialsRef.current.length) {
            if (blockIdxRef.current < BLOCKS.length - 1) startBlock(blockIdxRef.current + 1);
            else showCompletion();
            return;
        }
        const t = blockTrialsRef.current[trialInBlockRef.current];
        renderTrial(t, `${t.blockName} · ${(t.deadlineMs / 1000).toFixed(1)}s`);
        // renderTrial/startBlock close over refs; recreating this callback each render would reset the trial loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startBlock = (idx: number) => {
        blockIdxRef.current = idx;
        const b = BLOCKS[idx];
        blockTrialsRef.current = generateBlock(b);
        trialInBlockRef.current = 0;
        setModal({
            title: `Block ${idx + 1}: ${b.name}`,
            body: `Cards stay for ${(b.deadline / 1000).toFixed(1)}s each.\n\nRead the message, not its color. Urgent → Respond Now. Routine → Wait. Don't tap spam.`,
            actionLabel: 'Begin block',
            onConfirm: () => launchTrial(),
        });
    };

    const startPracticeStep = () => {
        if (practiceStepRef.current >= PRACTICE.length) {
            setModal({
                title: 'Practice complete!',
                body: "Nice, you've got the idea.\n\nReady for Block 1: Warm-up (6.0s per notification)?",
                actionLabel: 'Start Warm-up',
                onConfirm: () => {
                    isPracticeRef.current = false;
                    startBlock(0);
                },
            });
            return;
        }
        const p = PRACTICE[practiceStepRef.current];
        const t: ITrial = { ...p, deadlineMs: 4500, blockName: 'Practice' };
        renderTrial(t, `Practice · ${practiceStepRef.current + 1}/3`);
    };

    const respond = (action: TAction) => {
        if (!acceptingRef.current && action !== null) return;
        acceptingRef.current = false;
        if (animRef.current) cancelAnimationFrame(animRef.current);
        const t = trialRef.current;
        if (!t) return;
        const rt = action ? Math.round(performance.now() - trialStartRef.current) : null;
        const isSpam = t.meaning === 'spam';
        let outcome: string;
        if (isSpam) outcome = action === null ? 'correct-inhibition' : 'commission-error';
        else if (action === null) outcome = 'missed-timeout';
        else if (action === t.correct) outcome = 'hit';
        else outcome = 'wrong-category';

        if (outcome === 'hit' || outcome === 'correct-inhibition') playSound(659, 0.1);
        else playSound(220, 0.2, 'sawtooth');

        if (isPracticeRef.current) {
            const ok = outcome === 'hit' || outcome === 'correct-inhibition';
            setModal({
                title: ok ? 'Well done!' : 'Watch out!',
                body: t.explanation || '',
                actionLabel: 'Next step',
                badgeText: ok ? '✓ Correct action' : '✕ Incorrect action',
                badgeCorrect: ok,
                onConfirm: () => {
                    practiceStepRef.current += 1;
                    startPracticeStep();
                },
            });
            return;
        }

        logRef.current.push({ meaning: t.meaning, congruency: t.congruency, computedOutcome: outcome, responseTimeMs: rt });
        trialInBlockRef.current += 1;
        window.setTimeout(() => launchTrial(), 160);
    };

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (screen !== 'game' || modal || !acceptingRef.current) return;
            const k = e.key.toLowerCase();
            if (e.key === 'ArrowLeft' || k === 'a') respond('respond_now');
            else if (e.key === 'ArrowRight' || k === 'd') respond('wait');
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screen, modal]);

    useEffect(() => () => {
        if (animRef.current) cancelAnimationFrame(animRef.current);
    }, []);

    return (
        <div className="mcsa_game__notification_rush">
            <div className="mcsa_game__notification_rush__phone">
                <div className="mcsa_game__notification_rush__notch" />
                <div className="mcsa_game__notification_rush__status">
                    <span>{clock}</span>
                    <button type="button" onClick={() => setSoundOn((v) => !v)} aria-label="Toggle sound">
                        {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
                    </button>
                </div>

                {screen === 'intro' && (
                    <main className="mcsa_game__notification_rush__intro">
                        <div>
                            <div className="mcsa_game__notification_rush__clock">{clock}</div>
                            <div className="mcsa_game__notification_rush__date">{date}</div>
                        </div>
                        <div className="mcsa_game__notification_rush__card_copy">
                            <h2>Notification Rush</h2>
                            <p>Incoming notifications will land one at a time. Decide fast - Urgent or Can Wait?</p>
                            <ul>
                                <li>Filter urgent messages → <strong>Respond Now</strong>. Routine messages → <strong>Wait till Later</strong>.</li>
                                <li>Spam or junk? <strong>Do nothing</strong> and let the timer ring drain.</li>
                                <li>Your reaction time matters.</li>
                                <li>Use the buttons or ← / → (also A / D).</li>
                            </ul>
                        </div>
                        <div className="mcsa_game__notification_rush__intro_actions">
                            <button
                                type="button"
                                className="mcsa_game__notification_rush__cta"
                                onClick={() => {
                                    isPracticeRef.current = true;
                                    practiceStepRef.current = 0;
                                    logRef.current = [];
                                    setScreen('game');
                                    startPracticeStep();
                                }}
                            >
                                Start practice · 3 steps
                            </button>
                            <button
                                type="button"
                                className="mcsa_game__notification_rush__ghost"
                                onClick={() => {
                                    isPracticeRef.current = false;
                                    logRef.current = [];
                                    setScreen('game');
                                    startBlock(0);
                                }}
                            >
                                Skip practice
                            </button>
                        </div>
                    </main>
                )}

                {screen === 'game' && trial && (
                    <main className="mcsa_game__notification_rush__play">
                        <div className="mcsa_game__notification_rush__badge">{badge}</div>
                        <div className="mcsa_game__notification_rush__stage">
                            <svg viewBox="0 0 380 180" preserveAspectRatio="none" className="mcsa_game__notification_rush__ring">
                                <rect x="4" y="4" width="372" height="172" rx="26" fill="none" stroke="rgba(255,255,255,.12)" strokeWidth="4" />
                                <rect
                                    x="4" y="4" width="372" height="172" rx="26" fill="none" stroke="#B284FF" strokeWidth="5" strokeLinecap="round"
                                    strokeDasharray={strokeLen} strokeDashoffset={ringOffset}
                                />
                            </svg>
                            <article className="mcsa_game__notification_rush__note">
                                <div className="mcsa_game__notification_rush__note_head">
                                    <span>{trial.sender}</span>
                                    <span>now</span>
                                </div>
                                <p style={{ color: COLOR_HEX[trial.colorShown] }}>{trial.text}</p>
                            </article>
                        </div>
                        <div className="mcsa_game__notification_rush__actions">
                            <button type="button" className="mcsa_game__notification_rush__urgent" onClick={() => respond('respond_now')}>
                                <span>Respond Now</span>
                                <span>← Left</span>
                            </button>
                            <button type="button" className="mcsa_game__notification_rush__wait" onClick={() => respond('wait')}>
                                <span>Wait till Later</span>
                                <span>Right →</span>
                            </button>
                        </div>
                        <p className="mcsa_game__notification_rush__hint">Spam or junk? Do nothing — let the ring drain.</p>
                    </main>
                )}

                {screen === 'complete' && stats && (
                    <main className="mcsa_game__notification_rush__done">
                        <div className="mcsa_game__notification_rush__party">🎉</div>
                        <h1>Session complete!</h1>
                        <p>You cleared all three notification blocks.</p>
                        <div className="mcsa_game__notification_rush__stats">
                            <div><strong>{stats.accuracy}</strong><span>Overall accuracy</span></div>
                            <div><strong>{stats.spam}</strong><span>Spam ignored</span></div>
                        </div>
                        <div className="mcsa_game__notification_rush__insight">
                            <h3>{stats.affirmationTitle}</h3>
                            <p>{stats.insightText}</p>
                        </div>
                        <p className="mcsa_game__notification_rush__closing">Your full cognitive breakdown lands in your final report.</p>
                        <button type="button" className="mcsa_game__notification_rush__cta" onClick={onComplete}>Ready to take off!</button>
                    </main>
                )}

                {modal && (
                    <div className="mcsa_game__notification_rush__modal">
                        <div className="mcsa_game__notification_rush__modal_card">
                            {modal.badgeText && (
                                <div className={`mcsa_game__notification_rush__modal_badge ${modal.badgeCorrect ? 'is-ok' : 'is-bad'}`}>
                                    {modal.badgeText}
                                </div>
                            )}
                            <h2>{modal.title}</h2>
                            <p>{modal.body}</p>
                            <button
                                type="button"
                                className="mcsa_game__notification_rush__cta"
                                onClick={() => {
                                    const cb = modal.onConfirm;
                                    setModal(null);
                                    cb();
                                }}
                            >
                                {modal.actionLabel}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
